<template>
    <div class="h-full space-y-6">
        <h2 class="text-heading-md">Upload Component Examples</h2>

        <div class="flex w-full items-center gap-2">
            <DsSingleSelect
                v-model="sizeSelection"
                id="height-variant"
                class="w-1/2"
                label="Height Variant"
                :options="sizeSelectionOptions"
                placeholder="Select size"
            />

            <DsCardCheckbox
                v-model="useSmallFileSize"
                label="Yes"
                name="use-small-file-size"
                value="yes"
            >
                Allow only &lt; 2kb files
            </DsCardCheckbox>

            <DsCardCheckbox
                v-model="isLoading"
                label="Display loading state"
                name="display-loading-state"
                value="yes"
            >
                Display loading state
            </DsCardCheckbox>

            <DsCardCheckbox
                v-model="isDisabled"
                label="Display disabled state"
                name="display-disabled-state"
                value="yes"
            >
                Display disabled state
            </DsCardCheckbox>
        </div>
        <div class="mt-8 rounded-lg bg-ds-gray-100 p-4">
            <h3 class="mb-2 text-content-md font-semibold">Last Event:</h3>
            <pre class="text-content-sm">{{ lastEvent ? JSON.stringify(lastEvent, null, 2) : 'No events yet' }}</pre>
        </div>
        <div class="mt-8 rounded-lg bg-ds-gray-100 p-4">
            <h3 class="mb-2 text-content-md font-semibold">Last Upload Trigger:</h3>
            <pre class="text-content-sm">{{ lastUploadTrigger ? lastUploadTrigger : 'No upload triggers yet' }}</pre>
        </div>
        <div
            v-if="sizeSelection"
            class="grid h-full flex-1 grid-cols-2 gap-8"
        >
            <div class="h-full">
                <h3 class="mb-3 text-content-lg font-semibold">{{ sizeSelection.label }} Mode</h3>

                <div class="h-full max-w-2xl">
                    <DsUpload
                        v-model:fileList="bigModeFiles"
                        :dataTypes="commonDocTypes"
                        :disabled="isDisabled"
                        :isLoading
                        :maxFileSize="useSmallFileSize ? toBytes(2, 'KB') : toBytes(10, 'MB')"
                        multiple
                        :size="sizeSelection.size ?? 'lg'"
                        @fileUploadError="onFileError"
                        @fileUploaded="onFileUploaded"
                        @upload-triggered="onUploadTriggered"
                    />
                    <div
                        v-if="bigModeFiles.length > 0"
                        class="mt-4"
                    >
                        <h4 class="mb-2 text-content-md font-semibold">Uploaded Files:</h4>
                        <ul class="space-y-1">
                            <li
                                v-for="(doc, index) in bigModeFiles"
                                :key="index"
                                class="text-content-sm"
                            >
                                {{ doc.file.name }} ({{ formatFileSize(doc.file.size) }})
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div>
                <h3 class="mb-3 text-content-lg font-semibold">With Required Validation</h3>
                <div class="h-full max-w-2xl">
                    <DsUpload
                        v-model:error="errorModeError"
                        v-model:fileList="errorModeFiles"
                        :dataTypes="commonDocTypes"
                        :disabled="isDisabled"
                        :isLoading
                        multiple
                        required
                        :size="sizeSelection.size ?? 'lg'"
                        @fileUploadError="onFileError"
                        @fileUploaded="onFileUploaded"
                        @upload-triggered="onUploadTriggered"
                    />
                </div>
            </div>
            <div>
                <h3 class="mb-3 text-content-lg font-semibold">Invoked via slotted click trigger</h3>

                <DsUpload
                    v-model:fileList="externallyInvokedFiles"
                    :maxFileSize="useSmallFileSize ? toBytes(2, 'KB') : toBytes(10, 'MB')"
                    :size="externallyInvokedFiles.length > 0 ? sizeSelection.size : 'collapsed'"
                    @fileUploadError="onFileError"
                    @fileUploaded="onFileUploaded"
                    @uploadTriggered="onUploadTriggered"
                >
                    <template
                        v-if="externallyInvokedFiles.length === 0"
                        #clickTrigger="{ triggerFilePicker }"
                    >
                        <DsButton
                            size="md"
                            variant="primary"
                            @click="triggerFilePicker"
                        >
                            Open picker and show dropzone
                        </DsButton>
                    </template>
                </DsUpload>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsCardCheckbox from '@/components/design-system/form/input/DsCardCheckbox.vue';
import DsSingleSelect from '@/components/design-system/singleselect/DsSingleSelect.vue';
import type { DsSingleSelectOptionBase } from '@/components/design-system/singleselect/single-select.types';
import { DocumentDataType } from '@/components/design-system/upload/document-data-types';
import type { DsUploadEmitErrorReason, DsUploadProps, DsUploadTrigger } from '@/components/design-system/upload/ds-upload.types';
import { type StagedDocument } from '@/components/design-system/upload/ds-upload.types';
import DsUpload from '@/components/design-system/upload/DsUpload.vue';
import { formatFileSize, toBytes } from '@/shared/utils/file-size-convert';
import { ref } from 'vue';

const bigModeFiles = ref<StagedDocument[]>([]);
const errorModeFiles = ref<StagedDocument[]>([]);
const externallyInvokedFiles = ref<StagedDocument[]>([]);
const errorModeError = ref<string>('Add one or more documents');
const useSmallFileSize = ref(false);
const isLoading = ref(false);
const isDisabled = ref(false);

const sizeSelection = ref({
    id: 'lg',
    value: 'lg',
    label: 'Large',
    size: 'lg' as DsUploadProps['size'],
});
const sizeSelectionOptions: (DsSingleSelectOptionBase & { size: DsUploadProps['size'] })[] = [
    {
        id: 'full',
        label: 'Full (all parent height)',
        value: 'full',
        size: 'full',
    },
    {
        id: 'lg',
        label: 'Large',
        value: 'lg',
        size: 'lg',
    },
    {
        id: 'md',
        label: 'Medium',
        value: 'md',
        size: 'md',
    },
];

const lastEvent = ref<unknown>(null);
const lastUploadTrigger = ref<DsUploadTrigger | null>(null);

const commonDocTypes = [
    DocumentDataType.DOC,
    DocumentDataType.DOCX,
    DocumentDataType.PDF,
    DocumentDataType.JPEG,
    DocumentDataType.PNG,
];

const onFileUploaded = (payload: { file: File }) => {
    lastEvent.value = {
        type: 'file-uploaded',
        fileName: payload.file.name,
        fileSize: payload.file.size,
        fileType: payload.file.type,
    };
};

const onFileError = (payload: { file: File; reason: DsUploadEmitErrorReason }) => {
    lastEvent.value = {
        type: 'file-upload-error',
        fileName: payload.file.name,
        fileSize: payload.file.size,
        fileType: payload.file.type,
        reason: payload.reason,
    };
};

const onUploadTriggered = (source: DsUploadTrigger) => {
    lastUploadTrigger.value = source;
};
</script>
